import { createContext, use, type ReactNode } from 'react';
import type { EisApiClients } from './clients';

const ApiClientsContext = createContext<EisApiClients | null>(null);

/**
 * Hands the SDK clients to the query hooks.
 *
 * A context rather than a module-level singleton, because the clients carry a token provider and
 * an ETag store — session state. A singleton would outlive a sign-out, and the next operator
 * would inherit the previous one's beliefs about which version of a policy they had read.
 */
export function ApiClientsProvider({
  clients,
  children,
}: {
  readonly clients: EisApiClients;
  readonly children: ReactNode;
}): ReactNode {
  return <ApiClientsContext value={clients}>{children}</ApiClientsContext>;
}

export function useApiClients(): EisApiClients {
  const clients = use(ApiClientsContext);
  if (clients === null) {
    throw new Error('useApiClients must be used inside <ApiClientsProvider>.');
  }
  return clients;
}

export { ApiClientsContext };
